import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2, Clock, ArrowRight, Home } from 'lucide-react';
import { fbTrackPurchase } from '../lib/fbpixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentReturn = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get('order_id');
  const purchaseTrackedRef = useRef(false);

  const [state, setState] = useState({ loading: true, status: null, data: null, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!orderId) {
      setState({ loading: false, status: 'ERROR', error: 'Missing order_id in URL', data: null });
      return;
    }

    let cancelled = false;
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/cashfree/order-status/${encodeURIComponent(orderId)}`);
        if (cancelled) return;
        const status = (res.data?.status || '').toUpperCase();
        // PENDING -> retry up to ~6 times every 2s
        if (status === 'PENDING' && attempt < 6) {
          setTimeout(() => setAttempt((a) => a + 1), 2000);
          setState({ loading: true, status: 'PENDING', data: res.data, error: null });
        } else {
          // Fire Facebook Pixel Purchase event (once per order)
          if (status === 'APPROVED' && !purchaseTrackedRef.current) {
            const trackedKey = `fb_purchase_${orderId}`;
            if (typeof window !== 'undefined' && !window.sessionStorage.getItem(trackedKey)) {
              try {
                fbTrackPurchase({
                  value: Number(res.data?.amount) || 0,
                  content_name: res.data?.plan || 'Dropshipping Plan',
                  content_ids: [res.data?.plan || 'plan'],
                  transaction_id: res.data?.order_id || orderId,
                });
                window.sessionStorage.setItem(trackedKey, '1');
                purchaseTrackedRef.current = true;
              } catch (e) {
                // silent
              }
            }
          }
          setState({ loading: false, status, data: res.data, error: null });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          status: 'ERROR',
          error: err.response?.data?.detail || err.message || 'Verification failed',
          data: null,
        });
      }
    };
    verify();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, attempt]);

  const renderContent = () => {
    if (state.loading) {
      return (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Verifying your payment…</h2>
          <p className="text-slate-500">Please wait while we confirm your transaction.</p>
        </div>
      );
    }

    if (state.status === 'APPROVED') {
      return (
        <div className="text-center py-10 sm:py-12">
          <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/40 animate-bounce">
            <CheckCircle2 className="w-14 h-14 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 mb-2">Payment Successful 🎉</h2>
          <p className="text-slate-600 mb-6">Your {state.data?.plan || 'plan'} has been activated.</p>

          <div className="inline-block px-6 py-4 bg-blue-50 border border-blue-200 rounded-xl mb-6">
            <p className="text-xs text-slate-500">Order ID</p>
            <p className="text-base font-mono font-bold text-blue-700 break-all">{state.data?.order_id}</p>
            {state.data?.amount && (
              <p className="text-sm text-slate-500 mt-2">Amount: <span className="font-bold text-slate-800">₹{state.data.amount}</span></p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700 px-6 py-5 font-semibold">
              Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="border-slate-300 text-slate-700 px-6 py-5">
              <Home className="w-4 h-4 mr-2" /> Home
            </Button>
          </div>
        </div>
      );
    }

    if (state.status === 'REJECTED' || state.status === 'ERROR') {
      return (
        <div className="text-center py-10 sm:py-12">
          <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/40">
            <XCircle className="w-14 h-14 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-slate-800 mb-2">Payment Failed</h2>
          <p className="text-slate-600 mb-6">
            {state.error || 'Your payment could not be completed. No amount has been charged.'}
          </p>
          {state.data?.order_id && (
            <p className="text-xs text-slate-500 mb-6 font-mono">Order: {state.data.order_id}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate(-2)} className="bg-blue-600 hover:bg-blue-700 px-6 py-5 font-semibold">
              Try Again
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="border-slate-300 text-slate-700 px-6 py-5">
              <Home className="w-4 h-4 mr-2" /> Home
            </Button>
          </div>
        </div>
      );
    }

    // PENDING fallback
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-yellow-50 flex items-center justify-center">
          <Clock className="w-10 h-10 text-yellow-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Payment Pending</h2>
        <p className="text-slate-500 mb-6">
          We haven't received the confirmation yet. If amount was deducted it will be auto-updated shortly.
        </p>
        <Button onClick={() => setAttempt((a) => a + 1)} className="bg-blue-600 hover:bg-blue-700 px-6 py-5">
          Check Again
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <Card className="bg-white border-slate-200 shadow-xl">
          <CardContent className="p-4 sm:p-8">
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentReturn;
